import React, { useEffect, useState } from "react";
import {
  allyPortalApi,
  setToken,
} from "./api";
import {
  Button,
  Card,
  Input,
  Logo,
} from "./ui";

export default function AllyActivation() {
  const [status, setStatus] = useState("loading");
  const [invitation, setInvitation] = useState(null);
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] =
    useState("");
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  const token =
    typeof window !== "undefined"
      ? new URLSearchParams(
          window.location.hash.replace(/^#/, "")
        ).get("token") || ""
      : "";

  useEffect(() => {
    let mounted = true;

    async function loadInvitation() {
      if (!token) {
        setError(
          "Este enlace de invitación no es válido."
        );
        setStatus("error");
        return;
      }

      try {
        const data =
          await allyPortalApi.invitation(token);

        if (!mounted) return;

        setInvitation(data);
        setStatus("ready");
      } catch (e) {
        if (!mounted) return;

        setError(
          e?.message ||
            "No pudimos validar esta invitación."
        );
        setStatus("error");
      }
    }

    loadInvitation();

    return () => {
      mounted = false;
    };
  }, [token]);

  async function submit(e) {
    e.preventDefault();
    setError("");

    if (password.length < 6) {
      setError(
        "La contraseña debe tener al menos 6 caracteres."
      );
      return;
    }

    if (password !== confirmPassword) {
      setError("Las contraseñas no coinciden.");
      return;
    }

    setSaving(true);

    try {
      const result =
        await allyPortalApi.acceptInvitation(
          token,
          password
        );

      if (!result?.token) {
        throw new Error(
          "No pudimos completar la configuración."
        );
      }

      setToken(result.token);

      window.location.replace(
        result.redirect || "/aliados"
      );
    } catch (e2) {
      setError(
        e2?.message ||
          "No pudimos configurar tu acceso."
      );
    } finally {
      setSaving(false);
    }
  }

  return (
    <main
      className="min-h-screen bg-[#F8FAFC] px-4 py-8"
      data-testid="ally-activation"
    >
      <div className="mx-auto max-w-md">
        <Card className="p-6 sm:p-8">
          <div className="text-center">
            <Logo size="lg" />

            <p className="mt-3 text-xs font-black uppercase tracking-[0.18em] text-[#FF5C5C]">
              PawCita Aliados
            </p>

            <h1 className="mt-4 font-display text-3xl font-black text-[#0B2D4D]">
              Configura tu acceso
            </h1>
          </div>

          {status === "loading" ? (
            <p
              className="mt-6 text-center text-sm text-slate-500"
              data-testid="ally-activation-loading"
            >
              Validando invitación…
            </p>
          ) : null}

          {status === "error" ? (
            <div
              className="mt-6 rounded-2xl bg-red-50 p-4 text-sm text-red-700"
              data-testid="ally-activation-error"
            >
              <p className="font-bold">
                No pudimos abrir la invitación
              </p>

              <p className="mt-1">
                {error}
              </p>

              <p className="mt-3 text-xs">
                Solicita a PawCita una nueva
                invitación de acceso.
              </p>
            </div>
          ) : null}

          {status === "ready" ? (
            <>
              <div className="mt-6 rounded-2xl bg-[#F1FFFA] p-4">
                <p className="text-xs font-black uppercase tracking-wide text-[#6B7A90]">
                  Establecimiento
                </p>

                <p className="mt-1 text-lg font-black text-[#0B2D4D]">
                  {invitation?.ally_name}
                </p>

                <p className="mt-3 text-xs font-black uppercase tracking-wide text-[#6B7A90]">
                  Correo autorizado
                </p>

                <p
                  className="mt-1 break-all text-sm font-semibold text-[#0B2D4D]"
                  data-testid="ally-activation-email"
                >
                  {invitation?.email}
                </p>

                {invitation?.responsible_name ? (
                  <>
                    <p className="mt-3 text-xs font-black uppercase tracking-wide text-[#6B7A90]">
                      Responsable
                    </p>

                    <p className="mt-1 text-sm text-[#0B2D4D]">
                      {invitation.responsible_name}
                    </p>
                  </>
                ) : null}
              </div>

              <form
                className="mt-6 space-y-4"
                onSubmit={submit}
              >
                <Input
                  label="Crea tu contraseña"
                  type="password"
                  autoComplete="new-password"
                  value={password}
                  onChange={(e) =>
                    setPassword(e.target.value)
                  }
                  placeholder="Mínimo 6 caracteres"
                  testId="ally-activation-password"
                />

                <Input
                  label="Confirma tu contraseña"
                  type="password"
                  autoComplete="new-password"
                  value={confirmPassword}
                  onChange={(e) =>
                    setConfirmPassword(
                      e.target.value
                    )
                  }
                  placeholder="Repite tu contraseña"
                  testId="ally-activation-confirm"
                />

                {error ? (
                  <div
                    className="rounded-xl bg-red-50 p-3 text-sm text-red-700"
                    data-testid="ally-activation-form-error"
                  >
                    {error}
                  </div>
                ) : null}

                <Button
                  type="submit"
                  variant="navy"
                  disabled={saving}
                  className="w-full py-3.5"
                  testId="ally-activation-submit"
                >
                  {saving
                    ? "Configurando…"
                    : "Activar mi acceso"}
                </Button>
              </form>

              <p className="mt-5 text-center text-xs leading-5 text-slate-500">
                Este enlace es personal y de un solo uso.
                Después podrás ingresar normalmente en
                app.pawcita.com/aliados.
              </p>
            </>
          ) : null}
        </Card>
      </div>
    </main>
  );
}
